import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { FiMail, FiLock, FiArrowRight, FiCheck, FiAlertCircle, FiArrowLeft, FiKey } from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';

function LoginModern({ onLogin }) {
  const [email, setEmail] = useState('test@example.com');
  const [password, setPassword] = useState('password123');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  // Forgot password state
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [fpStep, setFpStep] = useState(1); // 1=email, 2=otp, 3=new password
  const [fpEmail, setFpEmail] = useState('');
  const [fpOtp, setFpOtp] = useState('');
  const [fpNewPassword, setFpNewPassword] = useState('');
  const [fpConfirmPassword, setFpConfirmPassword] = useState('');
  const [fpLoading, setFpLoading] = useState(false);
  const [fpError, setFpError] = useState('');
  const [fpSuccess, setFpSuccess] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/auth/login.php`,
        { email, password },
        { headers: { 'Content-Type': 'application/json' } }
      );

      if (response.data.success) {
        onLogin(response.data.token, response.data.user);
        const uType = response.data.user?.user_type;
        const redirectPath = (uType === 'super_admin' || uType === 'admin') ? '/admin' : '/dashboard';
        navigate(redirectPath);
      } else {
        setError(response.data.message || 'Login failed');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'An error occurred during login');
    } finally {
      setLoading(false);
    }
  };

  const benefits = [
    'Access your legal cases anytime',
    'Book consultations instantly',
    'Track case progress in real-time',
    'Secure end-to-end messaging',
  ];

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setFpError('');
    setFpSuccess('');
    setFpLoading(true);

    try {
      if (fpStep === 1) {
        // Request OTP
        const res = await axios.post(`${process.env.REACT_APP_API_URL}/api/auth/reset-password.php`, { action: 'request', email: fpEmail });
        if (res.data.success) {
          setFpSuccess('OTP sent to your email. Please check your inbox.');
          setFpStep(2);
        } else {
          setFpError(res.data.message || 'Failed to send OTP');
        }
      } else if (fpStep === 2) {
        // Verify OTP
        const res = await axios.post(`${process.env.REACT_APP_API_URL}/api/auth/reset-password.php`, { action: 'verify_otp', email: fpEmail, otp: fpOtp });
        if (res.data.success) {
          setFpSuccess('OTP verified! Set your new password.');
          setFpStep(3);
        } else {
          setFpError(res.data.message || 'Invalid OTP');
        }
      } else if (fpStep === 3) {
        // Reset Password
        if (fpNewPassword !== fpConfirmPassword) {
          setFpError('Passwords do not match');
          setFpLoading(false);
          return;
        }
        if (fpNewPassword.length < 6) {
          setFpError('Password must be at least 6 characters');
          setFpLoading(false);
          return;
        }
        const res = await axios.post(`${process.env.REACT_APP_API_URL}/api/auth/reset-password.php`, { action: 'reset', email: fpEmail, otp: fpOtp, new_password: fpNewPassword });
        if (res.data.success) {
          setFpSuccess('Password reset successfully! You can now login with your new password.');
          setTimeout(() => {
            setShowForgotPassword(false);
            resetForgotPassword();
          }, 2000);
        } else {
          setFpError(res.data.message || 'Failed to reset password');
        }
      }
    } catch (err) {
      setFpError(err.response?.data?.message || 'An error occurred');
    } finally {
      setFpLoading(false);
    }
  };

  const resetForgotPassword = () => {
    setFpStep(1);
    setFpEmail('');
    setFpOtp('');
    setFpNewPassword('');
    setFpConfirmPassword('');
    setFpError('');
    setFpSuccess('');
  };

  return (
    <>
    <div className="min-h-screen flex">
      {/* Left Panel */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #0f1f35 0%, #1e3a5f 50%, #172e4d 100%)' }}>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 right-20 w-96 h-96 rounded-full" style={{ background: 'radial-gradient(circle, rgba(212,175,55,0.4) 0%, transparent 70%)' }} />
        </div>
        <div className="relative z-10 flex flex-col justify-center px-16">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <Link to="/" className="flex items-center gap-2.5 mb-12">
              <div className="w-10 h-10 rounded-xl bg-accent-500 flex items-center justify-center">
                <span className="text-lg">⚖️</span>
              </div>
              <span className="text-xl font-bold text-white">Bihar Lawyer</span>
            </Link>

            <h1 className="text-4xl font-bold text-white mb-4 leading-tight">Welcome Back to<br/>Your Legal Hub</h1>
            <p className="text-white/50 text-lg mb-10 max-w-md">
              Sign in to access your legal consultation dashboard and manage your cases.
            </p>

            <div className="space-y-4">
              {benefits.map((item, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.3 + i * 0.1 }}
                  className="flex items-center gap-3"
                >
                  <div className="w-6 h-6 rounded-full bg-accent-500/20 flex items-center justify-center flex-shrink-0">
                    <FiCheck className="w-3.5 h-3.5 text-accent-400" />
                  </div>
                  <span className="text-white/70 text-sm">{item}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>

      {/* Right Panel - Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-secondary-50">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="lg:hidden mb-8 text-center">
            <Link to="/" className="inline-flex items-center gap-2.5">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center">
                <span className="text-lg">⚖️</span>
              </div>
              <span className="text-xl font-bold text-secondary-900">Bihar Lawyer</span>
            </Link>
          </div>

          <div className="bg-white rounded-2xl shadow-card p-8 border border-secondary-100">
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-secondary-900 mb-2">Sign In</h2>
              <p className="text-secondary-500 text-sm">Enter your credentials to access your account</p>
            </div>

            {error && (
              <div className="flex items-center gap-3 p-4 rounded-xl bg-danger-50 border border-danger-500/20 mb-6">
                <FiAlertCircle className="w-5 h-5 text-danger-500 flex-shrink-0" />
                <p className="text-sm text-danger-600">{error}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="label">Email Address</label>
                <div className="relative">
                  <FiMail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-secondary-400" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    required
                    className="input pl-11"
                  />
                </div>
              </div>

              <div>
                <label className="label">Password</label>
                <div className="relative">
                  <FiLock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-secondary-400" />
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                    className="input pl-11"
                  />
                </div>
              </div>

              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" defaultChecked className="w-4 h-4 rounded border-secondary-300 text-primary-500 focus:ring-primary-500" />
                  <span className="text-sm text-secondary-600">Remember me</span>
                </label>
                <button
                  type="button"
                  onClick={() => setShowForgotPassword(true)}
                  className="text-sm font-medium text-primary-500 hover:text-primary-600 transition-colors"
                >
                  Forgot password?
                </button>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full btn-primary py-3.5 text-base gap-2"
              >
                {loading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Signing in...
                  </>
                ) : (
                  <>
                    Sign In
                    <FiArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
            </form>

            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-secondary-200" />
              </div>
              <div className="relative flex justify-center">
                <span className="bg-white px-4 text-sm text-secondary-400">or continue with</span>
              </div>
            </div>

            <button type="button" className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl border border-secondary-200 text-secondary-700 font-medium hover:bg-secondary-50 transition-colors text-sm">
              <span className="text-lg">🔍</span>
              Google
            </button>

            <p className="text-center text-sm text-secondary-500 mt-6">
              Don't have an account?{' '}
              <Link to="/signup" className="font-semibold text-primary-500 hover:text-primary-600 transition-colors">
                Create Account
              </Link>
            </p>

            <div className="mt-4 p-3 rounded-lg bg-secondary-50 text-center">
              <p className="text-xs text-secondary-400">
                Demo: test@example.com / password123
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </div>

    {/* Forgot Password Modal */}
    <AnimatePresence>
      {showForgotPassword && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <motion.div initial={{ opacity: 0, scale: 0.95, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95, y: 20 }} className="bg-white rounded-2xl shadow-xl max-w-md w-full p-8 relative">
            <button onClick={() => { setShowForgotPassword(false); resetForgotPassword(); }} className="absolute top-4 right-4 w-8 h-8 rounded-lg bg-secondary-100 flex items-center justify-center text-secondary-500 hover:bg-secondary-200 transition-colors">✕</button>
            
            <div className="text-center mb-6">
              <div className="w-14 h-14 rounded-2xl bg-primary-50 flex items-center justify-center mx-auto mb-4">
                <FiKey className="w-7 h-7 text-primary-500" />
              </div>
              <h3 className="text-xl font-bold text-secondary-900">
                {fpStep === 1 && 'Reset Password'}
                {fpStep === 2 && 'Enter OTP'}
                {fpStep === 3 && 'New Password'}
              </h3>
              <p className="text-sm text-secondary-500 mt-1">
                {fpStep === 1 && 'Enter your email to receive a verification code'}
                {fpStep === 2 && 'Enter the 6-digit code sent to your email'}
                {fpStep === 3 && 'Create a new password for your account'}
              </p>
            </div>

            {/* Step Indicator */}
            <div className="flex items-center justify-center gap-2 mb-6">
              {[1, 2, 3].map(step => (
                <div key={step} className="flex items-center gap-2">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-colors ${fpStep >= step ? 'bg-primary-500 text-white' : 'bg-secondary-100 text-secondary-400'}`}>
                    {fpStep > step ? <FiCheck className="w-4 h-4" /> : step}
                  </div>
                  {step < 3 && <div className={`w-8 h-0.5 ${fpStep > step ? 'bg-primary-500' : 'bg-secondary-200'}`} />}
                </div>
              ))}
            </div>

            {fpError && (
              <div className="flex items-center gap-3 p-3 rounded-xl bg-danger-50 border border-danger-500/20 mb-4">
                <FiAlertCircle className="w-4 h-4 text-danger-500 flex-shrink-0" />
                <p className="text-sm text-danger-600">{fpError}</p>
              </div>
            )}

            {fpSuccess && (
              <div className="flex items-center gap-3 p-3 rounded-xl bg-green-50 border border-green-200 mb-4">
                <FiCheck className="w-4 h-4 text-green-600 flex-shrink-0" />
                <p className="text-sm text-green-700">{fpSuccess}</p>
              </div>
            )}

            <form onSubmit={handleForgotPassword} className="space-y-4">
              {fpStep === 1 && (
                <div>
                  <label className="label">Email Address</label>
                  <div className="relative">
                    <FiMail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-secondary-400" />
                    <input type="email" value={fpEmail} onChange={(e) => setFpEmail(e.target.value)} placeholder="your@email.com" required className="input pl-11" />
                  </div>
                </div>
              )}

              {fpStep === 2 && (
                <div>
                  <label className="label">Verification Code (OTP)</label>
                  <input type="text" value={fpOtp} onChange={(e) => setFpOtp(e.target.value)} placeholder="Enter 6-digit OTP" required maxLength={6} className="input text-center text-2xl tracking-widest font-mono" />
                  <button type="button" onClick={() => { setFpStep(1); setFpError(''); setFpSuccess(''); }} className="text-sm text-primary-500 hover:text-primary-600 mt-2 flex items-center gap-1">
                    <FiArrowLeft className="w-3 h-3" /> Resend OTP
                  </button>
                </div>
              )}

              {fpStep === 3 && (
                <>
                  <div>
                    <label className="label">New Password</label>
                    <div className="relative">
                      <FiLock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-secondary-400" />
                      <input type="password" value={fpNewPassword} onChange={(e) => setFpNewPassword(e.target.value)} placeholder="Min 6 characters" required className="input pl-11" />
                    </div>
                  </div>
                  <div>
                    <label className="label">Confirm Password</label>
                    <div className="relative">
                      <FiLock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-secondary-400" />
                      <input type="password" value={fpConfirmPassword} onChange={(e) => setFpConfirmPassword(e.target.value)} placeholder="Re-enter password" required className="input pl-11" />
                    </div>
                  </div>
                </>
              )}

              <button type="submit" disabled={fpLoading} className="w-full btn-primary py-3 text-sm gap-2">
                {fpLoading ? (
                  <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Processing...</>
                ) : (
                  <>
                    {fpStep === 1 && 'Send Verification Code'}
                    {fpStep === 2 && 'Verify OTP'}
                    {fpStep === 3 && 'Reset Password'}
                    <FiArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
            </form>

            <button onClick={() => { setShowForgotPassword(false); resetForgotPassword(); }} className="w-full text-center text-sm text-secondary-500 hover:text-secondary-700 mt-4 transition-colors">
              Back to Login
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
    </>
  );
}

export default LoginModern;
